'use client'

import { dateToStringAgo } from '@repo/sdk'
import React, { useEffect, useState } from 'react'

interface Props {
  dateTime: Date | string
  onClick?: () => void
}

export const TimeAgo = ({ dateTime, onClick }: Props) => {
  const [trigger, setTrigger] = useState(0) // State to trigger re-renders
  const [intervalDuration, setIntervalDuration] = useState(1000) // State to manage interval duration

  useEffect(() => {
    const updateInterval = () => {
      const now = new Date()
      const targetDate = new Date(dateTime)

      const diffInSeconds = Math.abs((now.getTime() - targetDate.getTime()) / 1000)
      const diffInMinutes = diffInSeconds / 60
      const diffInHours = diffInMinutes / 60
      const diffInDays = diffInHours / 24

      // Determine new interval duration based on the time difference
      if (diffInMinutes < 1) return 1000 // Update every second for "now"
      if (diffInMinutes < 60) return 60 * 1000 // Update every minute
      if (diffInHours < 24) return 60 * 60 * 1000 // Update every hour
      if (diffInDays >= 1) {
        // Time until midnight for exact daily updates
        const nextDay = new Date()
        nextDay.setHours(0, 0, 0, 0) // Start of next day
        nextDay.setDate(nextDay.getDate() + 1)
        return nextDay.getTime() - now.getTime()
      }

      return 60 * 60 * 1000 // Fallback to hourly updates
    }

    // Update interval state dynamically
    const newInterval = updateInterval()
    setIntervalDuration(newInterval)
  }, [dateTime, trigger]) // Recalculate interval when `dateTime` changes

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTrigger(prev => prev + 1) // Trigger re-render
    }, intervalDuration)

    return () => clearInterval(intervalId) // Cleanup on unmount or interval change
  }, [intervalDuration]) // Reset interval when `intervalDuration` changes

  return (
    <p className="text-sm" onClick={onClick}>
      {dateToStringAgo(dateTime)}
    </p>
  )
}
